import React, {FC, useEffect, useMemo, useState} from "react";
import {useApi} from "../api/APIContext";
import {TeamAvailability} from "../api/dto";
import {Workorder} from "../../../lib/src/api/dto/Workorder";
import {Employee} from "../../../lib/src/api/dto/Employee"
import {Team} from "../../../lib/src/api/dto/Team"
import moment, {Moment} from "moment";
import {useTeamAvailability} from "../planning/hooks/useTeamAvailability";
import {useSelectedDay} from "../planning/DateContext";
import {EnrichedTeamDayAvailability} from "../planning/components/WeekdayPlanner";
import {HoursInput} from "./HoursInput";
import {Button, IconButton} from "@traas/lib/src/components/form/Button";
import { Callout } from "@traas/lib/src/components/content/Callout";
import { useModal } from "@traas/lib/src/components/layout/ModalProvider";
import {BulkHoursOverview} from "../modals/BulkHoursOverview";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp, faClock, faPencil, faPlus, faReceipt} from "@fortawesome/free-solid-svg-icons";
import {faArrowUpRightFromSquare} from "@fortawesome/pro-solid-svg-icons";
import {WorkorderDetailsModal} from "../modals/WorkorderDetailsModal";
import {EditHourRemarkModal} from "../modals/EditHourRemarkModal";
import {TimeRegistration} from "../../../lib/src/api/dto/TimeRegistration";
import {AddHourEntryModal} from "../modals/AddHourEntryModal";

export const HourOverview: FC<{}> = (props) => {
  const {day} = useSelectedDay()
  const {hours, teams, employees} = useApi()
  const dayParsed = useMemo(() => moment(day), [day])
  const ta = useTeamAvailability()
  const relevantEntries = hours.filter(entry =>
    entry.date.getFullYear() === day.getFullYear() &&
    entry.date.getMonth() === day.getMonth() &&
    entry.date.getDate() === day.getDate()
  );
  return <div className={"max-w-(--breakpoint-2xl) mx-auto"}>
    <HourManager timeEntries={relevantEntries} teamAvailability={ta} employees={employees} teams={teams} day={dayParsed} />
  </div>
}

const HourManager: FC<{ timeEntries: TimeRegistration[], teamAvailability: TeamAvailability|null, employees: Employee[], teams: Team[], day: Moment}> = (props) => {
  const todayTeams = useMemo(() => {
    return props.teams.filter(t => {
      return t.endedAt === null || props.day.isBetween(moment(t.startedAt), moment(t.endedAt), 'day', '[]');
    });
  }, [props.teams, props.day])
  const bulkHoursModal = useModal({title: 'Totaal uren overzicht', body: <BulkHoursOverview hours={props.timeEntries} employees={props.employees} />, size:'lg'})
  return <div>
    <Callout>
      <div className={'flex justify-between items-center'}>
        <div className={'flex-1'}>
          <h2 className={'text-lg font-bold'}>Overzicht exporteren</h2>
          <div>Exporteer een overzicht over een gegeven periode.</div>
        </div>
        <Button type={'primary'} size={'md'} text={'Exporteren'} onClick={() => {bulkHoursModal.open()}} />
      </div>
    </Callout>
    <div className={'flex justify-between'}>
      <h1 className={"font-bold text-2xl"}>{props.day.toDate().toLocaleDateString('nl', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })}</h1>
    </div>
    {todayTeams.map((team, i) => {
      return <TeamHours key={i} team={team} teamAvailability={props.teamAvailability} timeEntries={props.timeEntries} day={props.day} />
    })}
  </div>
}

const TeamHours: FC<{ team: Team, teamAvailability: TeamAvailability|null, timeEntries: TimeRegistration[], day: Moment }> = (props) => {
  const {teams, employees, workorders} = useApi()
  const tdas = useMemo(() => {
    if (props.day.isoWeekday() === 0) return props.teamAvailability?.sunday ?? []
    if (props.day.isoWeekday() === 1) return props.teamAvailability?.monday ?? []
    if (props.day.isoWeekday() === 2) return props.teamAvailability?.tuesday ?? []
    if (props.day.isoWeekday() === 3) return props.teamAvailability?.wednesday ?? []
    if (props.day.isoWeekday() === 4) return props.teamAvailability?.thursday ?? []
    if (props.day.isoWeekday() === 5) return props.teamAvailability?.friday ?? []
    if (props.day.isoWeekday() === 6) return props.teamAvailability?.saturday ?? []
    return []
  }, [props.teamAvailability, props.day])
  const enrichedTeamAvailabilities = useMemo(() => {
    const t = tdas.map(tda => {
      return {
        ...tda,
        team: tda.teamId === 'absence' ? new Team(
          'absence',
          'Afwezig',
          new Date(),
          null,
          tda.employeeMemberships,
          999,
          null,
          false,
          '',
        ) : teams.find(t => t.id === tda.teamId),
        employees: employees.filter(e => tda.employeeIds.includes(e.id))
      }
    }) as EnrichedTeamDayAvailability[] | undefined
    return t?.sort((a, b) => a.team.order - b.team.order) ?? []
  }, [tdas, teams, employees]);

  const tda = enrichedTeamAvailabilities.find(tda => tda.teamId === props.team.id)

  return <div className={"my-2"}>
    {!tda ? !! tdas ? <></> : <TeamHoursSkeleton /> :
    <>
      <h1 className={"text-xl font-bold"}>{tda?.team.name}</h1>
      <div className={"mt-3 mb-8 py-1 pl-3 bg-white rounded-sm border border-slate-200"}>
        {tda?.employees.length === 0 && <div className={'text-sm text-slate-600 my-4'}>
          Niemand ingepland
        </div>}
        <table>
          <tbody>
            {tda?.employees.map((employee, j) => {
              const employeeTimes = props.timeEntries.filter((entry) => entry.employeeId === employee.id)
              const relevantWorkorders = workorders.filter(order => employeeTimes.some(entry => entry.workorderId === order.workorderId));
              const otherWorkorders = workorders.filter(wo => wo.teamId === tda.teamId && wo.startAt && props.day.isSame(moment(wo.startAt), 'day') && ! relevantWorkorders.some(rwo => rwo.id === wo.id))
              return <EmployeeRow employee={employee} filteredTimes={employeeTimes} workorders={relevantWorkorders} otherWorkorders={otherWorkorders} day={props.day} key={j} />
            })}
          </tbody>
        </table>
      </div>
    </>
    }
  </div>
}

const EmployeeRow: FC<{ employee: Employee, filteredTimes: TimeRegistration[], workorders: Workorder[], otherWorkorders: Workorder[], day: Moment }> = (props) => {
  const [isCollapsable, setCollapsable] = useState<boolean>(false)
  const totalHours = props.filteredTimes.reduce((total, entry) => total + entry.hours, 0);
  const addTimeEntryModal = useModal({title: "Tijd schrijven", body: <AddHourEntryModal employee={props.employee} day={props.day} workorders={props.otherWorkorders} timeEntries={props.filteredTimes} />, size: "md"})
  useEffect(() => {
    setCollapsable(false);
  }, [props.day]);

  const entry = useMemo(() => props.filteredTimes.find(t => t.employeeId === props.employee.id && t.workorderId === null), [props.employee.id, props.filteredTimes])
  const editHourRemarkModal = useModal({title: 'Opmerking bewerken', body: <EditHourRemarkModal entries={props.filteredTimes} employee={props.employee} day={props.day} />, size: 'md'})
  return <>
    <tr>
      <td className={'w-full'}>{props.employee.firstName} {props.employee.lastName}</td>
      <td>
        <div className={'py-2 px-2 w-24 mr-6'}>
          <FontAwesomeIcon icon={faClock} className={'mr-1'} />
          <span className={'ml-1'}>{totalHours}</span>
        </div>
      </td>
      <td>
        <button className={'px-3 py-2 mr-2 rounded-sm hover:bg-slate-100'} onClick={() => setCollapsable(!isCollapsable)}>
          <FontAwesomeIcon icon={isCollapsable ? faChevronUp : faChevronDown} />
        </button>
      </td>
    </tr>
    {isCollapsable && <>
      <tr>
        <td colSpan={3} className={'pr-3'}><hr /></td>
      </tr>
      <tr>
        <td colSpan={3}>
          <div>
            <div className={'font-semibold py-1'}>Opmerking:</div>
            <div className={'flex items-start mr-2'}>
              <div className={'flex-1 mr-3 py-2 px-2 bg-slate-50 border border-slate-200 rounded-sm text-sm'}>{entry?.remark ?? '-'}</div>
              <IconButton size={'md'} type={'secondary'} icon={faPencil} onClick={() => {
                editHourRemarkModal.open()
              }}/>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td colSpan={3}>
          <div className={'mr-3'}>
            <div className={' font-semibold py-1 mt-2'}>Werkbonnen:</div>
            {props.filteredTimes.map((entry, e) => {
              const matchedWorkorder = props.workorders.find((order) => order.workorderId === entry.workorderId) ?? null
              // if (!matchedWorkorder) {
              //   return null;
              // }
              return <EmployeeHoursRow employee={props.employee} timeEntry={entry} workorder={matchedWorkorder} day={props.day} key={e} />
            })}

            <Button
              type={'secondary'}
              size={'md'}
              text={`Tijd schrijven`}
              icon={faPlus}
              onClick={() => {
                addTimeEntryModal.open()
              }}
            />
          </div>
        </td>
      </tr>
    </>}
  </>
}

const EmployeeHoursRow: FC<{ employee: Employee, timeEntry: TimeRegistration, workorder: Workorder|null, day: Moment }> = (props) => {
  const detailsModal = useModal({title: "Werkbon", size: 'xl', body: <WorkorderDetailsModal order={props.workorder!}/>})

  // Return nothing if neither a time or remark is set.
  if (props.workorder === null && props.timeEntry.hours === 0) {
    return <></>
  }

  //
  if (props.workorder === null) {
    return <div className={'flex items-center py-2 px-2 my-2 border border-slate-200 rounded-sm w-full'}>
      <div className={'flex flex-row flex-1 items-center'} onClick={() => detailsModal.open()}>
        <div className={'pr-2'}><FontAwesomeIcon icon={faReceipt} /></div>
        <div>Tijd zonder werkbon</div>
        {/*<div className={'ml-2 px-3 py-2 rounded-sm hover:bg-slate-100 hover:cursor-pointer hover:text-blue-900'}>*/}
        {/*  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />*/}
        {/*</div>*/}
      </div>
      <HoursInput employee={props.employee} time={props.timeEntry} day={props.day} />
    </div>
  }

  return <div className={'flex items-center py-2 px-2 my-2 border border-slate-200 rounded-sm w-full'}>
    <div className={'flex flex-row flex-1 items-center'} onClick={() => detailsModal.open()}>
      <div className={'pr-2'}><FontAwesomeIcon icon={faReceipt} /></div>
      <div>{props.workorder?.description ?? '??'}</div>
      <div className={'ml-2 px-3 py-2 rounded-sm hover:bg-slate-100 hover:cursor-pointer hover:text-blue-900'}>
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
      </div>
    </div>
    <HoursInput employee={props.employee} time={props.timeEntry} day={props.day} />
  </div>
}

const TeamHoursSkeleton: FC = () => {
  return <>
    <h1 className={"text-xl font-bold"}>&nbsp;</h1>
    <div className={"mt-3 mb-8 "}>
      <div className={"animated animated-pulse rounded-sm h-64 bg-slate-200"}> </div>
    </div>
  </>
}

